import { ComponentRef, useRef, useState } from "react";
import { useRouter } from "expo-router";
import { useSetAtom } from "jotai";

import { ApplicationNextStepCard } from "@/blocks/DashboardApplicationNextStepCard";
import { ApplicationSubmittedCard } from "@/blocks/DashboardApplicationSubmittedCard";
import { EligibilityNextStepCard } from "@/blocks/DashboardEligibilityNextStepCard";
import { RenewalGroupCard } from "@/blocks/DashboardRenewalGroupCard";
import { WorkRequirementCard } from "@/blocks/DashboardWorkRequirementCard";
import { HouseholdMemberSummaryCard } from "@/blocks/HouseholdMemberSummaryCard";
import { ScreenLayout } from "@/blocks/ScreenLayout";
import { SideSheetHeader } from "@/blocks/SideSheetHeader";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnScrollView } from "@/components/VnScrollView";
import { VnSheet, VnSheetContent } from "@/components/VnSheet";
import { VnText } from "@/components/VnText/VnText";
import { useDashboardCards } from "@/data/queries/dashboardQueries";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  DashboardCardType,
  shouldShowApplicationsSection,
  shouldShowRenewalsSection,
  shouldShowWorkRequirementsSection,
} from "@/domain/dashboardCards";
import { useIsDevToolsEnabled } from "@/hooks/useIsDevToolsEnabled";
import { useTranslator } from "@/hooks/useTranslate";
import { dashboardScenarioAtom } from "@/stores/atoms";

import { AddHouseholdMemberScreen } from "./AddHouseholdMemberScreen";
import { ApplicationsSection } from "./ApplicationsSection";
import { DashboardLayout } from "./DashboardLayout";
import { DashboardScenarioPanel } from "./DashboardScenarioPanel";
import { RedeterminationsSection } from "./RedeterminationsSection";
import { WorkRequirementsSection } from "./WorkRequirementsSection";

// The top card in the feed is the prominent primary CTA; cards below it use the
// compact secondary treatment. Exception: every "to start" renewal renders
// prominent, so multiple renewals-to-start stack as full primary cards.
const renderDashboardCard = (card: DashboardCardType, index: number) => {
  const isPrimary =
    index === 0 ||
    (card.kind === "RENEWAL_GROUP" && card.status === "NOT_STARTED");

  switch (card.kind) {
    case "ELIGIBILITY":
      return (
        <EligibilityNextStepCard
          key="ELIGIBILITY"
          state={card.state}
          isPrimary={isPrimary}
        />
      );
    case "APPLICATION_SUBMITTED":
      return (
        <ApplicationSubmittedCard key="APPLICATION_SUBMITTED" card={card} />
      );
    case "APPLICATION":
      return (
        <ApplicationNextStepCard
          key="APPLICATION"
          state={card.state}
          hasSubmittedApplications={card.hasSubmittedApplications}
          isPrimary={isPrimary}
        />
      );
    case "RENEWAL_GROUP":
      return (
        <RenewalGroupCard key={card.id} card={card} isPrimary={isPrimary} />
      );
    case "WORK_REQUIREMENT":
      return (
        <WorkRequirementCard key={card.id} card={card} isPrimary={isPrimary} />
      );
  }
};

const AddMemberButton = ({ className }: { className?: string }) => {
  const { t } = useTranslator();
  const scrollViewRef = useRef<ComponentRef<typeof VnScrollView>>(null);

  const [isAddingHouseholdMember, setIsAddingHouseholdMember] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setIsAddingHouseholdMember(isOpen);
  };

  const onMemberAddedSuccess = () => {
    setIsAddingHouseholdMember(false);
  };

  const handleAddAnother = () => {
    scrollViewRef.current?.scrollTo({ x: 0, y: 0, animated: true });
  };

  return (
    <>
      <VnButton
        className={className}
        i18nKey="native:homeScreen.addMember"
        variant="secondary"
        onPress={() => setIsAddingHouseholdMember(true)}
      />
      <VnSheet
        open={isAddingHouseholdMember}
        shouldShowCloseButton={false}
        overlayClassName="z-0"
        onOpenChange={handleOpenChange}>
        <VnSheetContent className="md:max-w-1/3">
          <SideSheetHeader title={t("native:homeScreen.addMember")} />
          <VnScrollView ref={scrollViewRef} className="flex-1">
            <VnBox className="gap-6">
              <AddHouseholdMemberScreen
                onSuccessOrContinue={onMemberAddedSuccess}
                continueSaveButtonI18nKey="common:save"
                onAddAnother={handleAddAnother}
              />
            </VnBox>
          </VnScrollView>
        </VnSheetContent>
      </VnSheet>
    </>
  );
};

const ScenarioActiveBanner = () => {
  const setScenario = useSetAtom(dashboardScenarioAtom);

  return (
    <VnBox className="flex flex-row items-center justify-between gap-3 rounded-md bg-yellow-100 px-4 py-3">
      <VnText
        i18nKey="native:homeScreen.devScenarioActive"
        className="flex-1"
      />
      <VnButton
        variant="secondary"
        i18nKey="native:homeScreen.devScenarioReset"
        onPress={() => setScenario(null)}
      />
    </VnBox>
  );
};

export const PatientDashboardScreen = () => {
  const { data: householdMembers } = useListHouseholdMembersQuery();
  const { cards, inputs, isScenarioActive } = useDashboardCards();
  const isDevToolsEnabled = useIsDevToolsEnabled();

  const router = useRouter();

  const hasFinishedEligibility =
    inputs.household?.eligibilityLastStep === "FINISHED";
  const hasDraftApplications =
    inputs.draftCounts.medicaid +
      inputs.draftCounts.medicare +
      inputs.draftCounts.marketplace >
    0;

  const handleRecheckEligibility = () => {
    router.navigate("/eligibility/recheck-eligibility");
  };

  return (
    <DashboardLayout>
      <ScreenLayout>
        <VnBox className="mt-safe-offset-10 web:py-4 w-full gap-8 md:max-w-screen-md md:self-center">
          {isScenarioActive && <ScenarioActiveBanner />}

          {cards.length > 0 && (
            <VnBox className="gap-4">
              <VnText
                variant="headerTwo"
                i18nKey="native:homeScreen.nextSteps"
              />
              {cards.map(renderDashboardCard)}
            </VnBox>
          )}

          <VnBox className="gap-4">
            <VnBox className="flex flex-col justify-between gap-2 md:flex-row md:items-center">
              <VnText
                variant="headerTwo"
                i18nKey="native:homeScreen.household"
              />
              {hasFinishedEligibility && (
                <VnBox className="flex flex-row items-center gap-4">
                  <VnBox className="flex-1 md:flex-none">
                    <VnButton
                      className="w-full md:w-auto"
                      i18nKey="native:homeScreen.recheckEligibility"
                      variant="secondary"
                      onPress={handleRecheckEligibility}
                    />
                  </VnBox>

                  <VnBox className="flex-1 md:flex-none">
                    <AddMemberButton className="w-full md:w-auto" />
                  </VnBox>
                </VnBox>
              )}
            </VnBox>

            <VnBox className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {householdMembers?.map(member => (
                <HouseholdMemberSummaryCard
                  key={member.id}
                  householdMember={member}
                />
              ))}
            </VnBox>
          </VnBox>

          {shouldShowWorkRequirementsSection(inputs) && (
            <WorkRequirementsSection />
          )}

          {shouldShowRenewalsSection(inputs) && <RedeterminationsSection />}

          {shouldShowApplicationsSection(inputs) && (
            <ApplicationsSection
              submittedApplications={inputs.submittedApplications}
              hasDraftApplications={hasDraftApplications}
            />
          )}
        </VnBox>
      </ScreenLayout>

      {isDevToolsEnabled && <DashboardScenarioPanel />}
    </DashboardLayout>
  );
};
